import { isSubscriptionRowPaidActive } from "@/lib/subscriptionActivation";

/** Statuts `dossiers.status` (API / base, souvent en anglais). */
export function dossierStatusLabelFr(status) {
  const s = String(status || "").trim().toLowerCase();
  const map = {
    pending: "En attente de validation",
    online: "En ligne",
    archived: "Archivée",
    hidden: "Masquée (marketplace)",
    withdrawn: "Retirée",
    retire: "Retirée",
    sold: "Vendue",
    vendu: "Vendue",
    rejected: "Refusée (modération)",
    reserved: "Réservée — achat en cours",
    draft: "Brouillon",
  };
  if (map[s]) return map[s];
  return s ? String(status).trim() : "—";
}

/** Badge liste / dashboard pour `dossiers.status`. */
export function annonceDossierStatusBadge(status) {
  const s = String(status || "").trim().toLowerCase();
  const map = {
    online: { label: "En ligne", className: "bg-emerald-50 text-emerald-800 ring-emerald-200/80" },
    draft: { label: "Brouillon", className: "bg-slate-100 text-slate-700 ring-slate-200/80" },
    sold: { label: "Vendue", className: "bg-slate-100 text-slate-600 ring-slate-200/80" },
    vendu: { label: "Vendue", className: "bg-slate-100 text-slate-600 ring-slate-200/80" },
    reserved: { label: "Réservée", className: "bg-amber-50 text-amber-900 ring-amber-200/80" },
    pending: { label: "En attente", className: "bg-amber-50 text-amber-900 ring-amber-200/80" },
    rejected: { label: "Refusée", className: "bg-red-50 text-red-800 ring-red-200/80" },
    archived: { label: "Archivée", className: "bg-slate-100 text-slate-600 ring-slate-200/80" },
    hidden: { label: "Masquée", className: "bg-slate-100 text-slate-700 ring-slate-200/80" },
    withdrawn: { label: "Retirée", className: "bg-slate-100 text-slate-600 ring-slate-200/80" },
  };
  const hit = map[s];
  if (hit) return hit;
  const label = dossierStatusLabelFr(status);
  return { label, className: "bg-slate-50 text-slate-700 ring-slate-200/80" };
}

/** Statuts `negotiations.status`. */
export function negotiationStatusLabelFr(status) {
  const s = String(status || "").trim().toLowerCase();
  const map = {
    open: "En cours",
    accepted: "Acceptée",
    rejected: "Refusée",
    paid: "Payée",
    cancelled: "Annulée",
  };
  if (map[s]) return map[s];
  return s ? String(status).trim() : "—";
}

/** Statuts d’offres de négociation (`negotiation_offers.status` ou équivalent). */
export function negotiationOfferStatusLabelFr(status) {
  const s = String(status || "").trim().toLowerCase();
  const map = {
    sent: "Envoyée",
    pending: "En attente",
    accepted: "Acceptée",
    rejected: "Refusée",
    countered: "Contre-offre",
    counter: "Contre-offre",
    withdrawn: "Retirée",
  };
  if (map[s]) return map[s];
  return s ? String(status).trim() : "—";
}

/** Statuts de ligne litige côté admin / API (`open`, `in_review`, `closed`…). */
export function litigeRowStatusLabelFr(status) {
  const s = String(status || "").trim().toLowerCase();
  const map = {
    open: "Ouvert",
    in_review: "En examen",
    closed: "Clos",
    clos: "Clos",
    resolu: "Résolu",
    résolu: "Résolu",
  };
  if (map[s]) return map[s];
  return s ? String(status).trim() : "—";
}

/** Rôle profil (`profiles.role`) — liste admin. */
export function profileRoleLabelFr(role) {
  const s = String(role || "").trim().toLowerCase();
  const map = {
    buyer: "Acheteur",
    seller: "Vendeur",
    admin: "Admin",
    utilisateur: "Utilisateur",
    user: "Utilisateur",
  };
  if (map[s]) return map[s];
  return s ? String(role).trim() : "—";
}

/** Abonnement résilié / inactif avec historique payant (liste admin, filtre « Résiliations »). */
export function isAdminUserSubscriptionChurned(u) {
  if (!u || u.subscription_active) return false;
  const st = String(u.subscription_statut || "").trim().toLowerCase();
  if (st === "cancelled" || st === "canceled") return true;
  if (u.subscription_canceled_at) return true;
  if (u.subscription_plan || u.subscription_statut) return true;
  return false;
}

function adminSubscriptionCanceledTitle(canceledAt) {
  if (!canceledAt) return "Abonnement résilié ou expiré";
  const t = Date.parse(String(canceledAt));
  if (!Number.isFinite(t)) return "Abonnement résilié ou expiré";
  const d = new Date(t).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  return `Résilié le ${d}`;
}

/** État abonnement à partir du détail admin (`detail.subscription` + `summary`). */
export function adminUserSubscriptionStatusFromDetail(detail) {
  const summary = detail?.summary || {};
  const sub = detail?.subscription || null;
  return adminUserSubscriptionStatusForList({
    is_deleted: summary.is_deleted,
    subscription_active: sub ? isSubscriptionRowPaidActive(sub) : false,
    subscription_statut: sub?.statut || null,
    subscription_plan: sub?.plan || null,
    subscription_canceled_at: sub?.canceled_at || null,
  });
}

/** Badge colonne « Abonnement » liste utilisateurs admin. */
export function adminUserSubscriptionStatusForList(u) {
  if (u?.is_deleted) {
    return {
      label: "Supprimé",
      badgeClass: "bg-slate-700 text-white",
      title: "Compte supprimé",
    };
  }
  if (u?.subscription_active) {
    const plan = String(u.subscription_plan || "").trim().toLowerCase();
    const planFr = plan === "yearly" ? "annuel" : plan === "monthly" ? "mensuel" : "";
    return {
      label: "Payant",
      badgeClass: "bg-emerald-100 text-emerald-800",
      title: planFr ? `Abonnement actif (${planFr})` : "Abonnement actif",
    };
  }
  if (isAdminUserSubscriptionChurned(u)) {
    const st = String(u.subscription_statut || "").trim();
    return {
      label: "Résilié",
      badgeClass: "bg-red-100 text-red-800",
      title: adminSubscriptionCanceledTitle(u.subscription_canceled_at) + (st ? ` (${st})` : ""),
    };
  }
  if (u?.subscription_statut) {
    const st = String(u.subscription_statut).trim();
    return {
      label: "Expiré",
      badgeClass: "bg-slate-100 text-slate-700",
      title: `Abonnement inactif (${st})`,
    };
  }
  return {
    label: "Sans abo",
    badgeClass: "bg-amber-100 text-amber-900",
    title: "Pas d’abonnement payant actif",
  };
}

/** Statuts KYC profils (`profiles.kyc_status`). */
export function kycStatusLabelFr(status) {
  const s = String(status || "").trim().toLowerCase();
  const map = {
    none: "Non commencée",
    pending: "En cours",
    approved: "Approuvée",
    rejected: "Refusée",
  };
  if (map[s]) return map[s];
  return s ? String(status).trim() : "—";
}
